/**
 * Firm-name helpers (multi-site aware).
 *
 * The `/me/profile` payload is inconsistent about the firm-name key: some responses carry
 * `firm.firmName`, others `firm.name`. Until the backend settles on one key, callers resolve the
 * display name through this single helper rather than repeating the fallback inline — so every place
 * that shows the current firm stays in sync and there's one spot to change when the API is fixed.
 *
 * In multi-site mode a user also needs to see WHICH site they're acting on inside a customer, so
 * {@link getSiteName} / {@link getCustomerAndSiteLabel} compose the customer name with the active
 * site. The active site lives on the auth store (`selectedSite.siteName`) — callers pass it in to
 * keep these utils dependency-free.
 */

/**
 * Customer (firm) display name — the row that spans every site. Prefer `firm.firmName`, fall
 * back to `firm.name`, else empty string.
 * @param {{ firm?: { firmName?: string, name?: string } } | null | undefined} profile - userStore profile
 * @returns {string}
 */
export function getFirmName(profile) {
  return profile?.firm?.firmName || profile?.firm?.name || '';
}

/**
 * Active site display name within the current customer. In single-site mode (no `selectedSite`
 * or a customer with only one site) returns the empty string so UI call sites can hide the badge
 * without extra guards.
 *
 * @param {{ haloPsaSiteId?: number, siteName?: string } | null | undefined} selectedSite
 *   from `useAuthStore(s => s.selectedSite)`
 * @param {{ firm?: { haloPsaSiteId?: number } } | null | undefined} [profile]
 *   optional fallback — used only when `selectedSite` is missing, to show the main site's label
 *   if the profile exposes it (today it exposes only the id, so this is forward-looking).
 * @returns {string}
 */
// eslint-disable-next-line no-unused-vars
export function getSiteName(selectedSite, profile) {
  return selectedSite?.siteName || '';
}

/**
 * Combined "customer — site" label for places that need to show both at once (e.g. header user
 * card, Profile Organization section). Falls back to just the customer name when no site is
 * selected, so single-site customers see the same label they used to.
 *
 * @param {{ firm?: { firmName?: string, name?: string } } | null | undefined} profile
 * @param {{ siteName?: string } | null | undefined} selectedSite
 * @returns {string}
 */
export function getCustomerAndSiteLabel(profile, selectedSite) {
  const firm = getFirmName(profile);
  const site = getSiteName(selectedSite, profile);
  if (firm && site) return `${firm} — ${site}`;
  return firm || site || '';
}
